import React, { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Modal,
  Typography,
  Space,
  Button,
  message,
  Alert,
} from "@mimber/ui-web"
import { Form } from "@mimber/forms"
import { MembershipTierSelect } from "@/components/selects"
import { useDeleteMembershipTier } from "@mimber/client/mimber-organization"
import { DeleteMembershipTierDtoSchema } from "@mimber/schema"
import type { DeleteMembershipTierModalProps } from "./DeleteMembershipTierModal.types"
import type { DeleteMembershipTierDto } from "@mimber/schema"

const { Text, Paragraph } = Typography

/**
 * Modal component for deleting membership tiers
 * Shows confirmation dialog with optional replacement tier selection
 */
export const DeleteMembershipTierModal: React.FunctionComponent<
  DeleteMembershipTierModalProps
> = ({ tier, onSuccess, onCancel, ...modalProps }) => {
  const [showReplacementSelect, setShowReplacementSelect] = useState(false)

  const form = useForm<DeleteMembershipTierDto>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(DeleteMembershipTierDtoSchema) as any,
    mode: "onBlur",
    defaultValues: {
      replacementTierUuid: undefined,
    },
  })

  const mutation = useDeleteMembershipTier({
    onSuccess: () => {
      message.success("Membership tier deleted successfully")
      onSuccess?.()
    },
    onError: (error) => {
      message.error(`Failed to delete membership tier: ${error.message}`)
    },
  })

  const onSubmit = (data: DeleteMembershipTierDto) => {
    mutation.mutate({
      tierUuid: tier.uuid,
      replacementTierUuid: data.replacementTierUuid,
    })
  }

  const handleQuickDelete = () => {
    mutation.mutate({
      tierUuid: tier.uuid,
    })
  }

  return (
    <Modal
      title="Delete Membership Tier"
      footer={null}
      onCancel={onCancel}
      {...modalProps}
    >
      <Form form={form} onSubmit={onSubmit} disabled={mutation.isPending}>
        <Space orientation="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <Paragraph>
              Are you sure you want to delete the membership tier{" "}
              <Text strong>"{tier.name}"</Text>?
            </Paragraph>
            <Alert
              message="Important"
              description="If members are assigned to this tier, they will need to be reassigned to a different tier. You can select a replacement tier below."
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          </div>

          {!showReplacementSelect && (
            <Button
              type="link"
              onClick={() => setShowReplacementSelect(true)}
              style={{ padding: 0 }}
            >
              + Select replacement tier for members
            </Button>
          )}

          {showReplacementSelect && (
            <>
              <MembershipTierSelect
                name="replacementTierUuid"
                label="Replacement Tier"
                placeholder="Select a tier to reassign members"
                excludeTierUuid={tier.uuid}
                help="All members on this tier will be moved to the selected tier"
              />
              <Button
                type="link"
                onClick={() => {
                  setShowReplacementSelect(false)
                  form.setValue("replacementTierUuid", undefined)
                }}
                style={{ padding: 0 }}
              >
                Remove replacement tier selection
              </Button>
            </>
          )}

          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={onCancel} disabled={mutation.isPending}>
              Cancel
            </Button>
            {showReplacementSelect ? (
              <Button
                htmlType="submit"
                type="primary"
                danger
                loading={mutation.isPending}
              >
                Delete & Reassign Members
              </Button>
            ) : (
              <Button
                type="primary"
                danger
                onClick={handleQuickDelete}
                loading={mutation.isPending}
              >
                Delete Tier
              </Button>
            )}
          </Space>
        </Space>
      </Form>
    </Modal>
  )
}
